// JSON-LD structured-data builders for Lone Star Counsel.
// Designed for AEO citation in ChatGPT / Perplexity / Google AI Overviews.

import { SITE } from "@/lib/seo";
import { OFFICES, FIRM_PHONE, MAIN_OFFICE_PHONE, PEAK_PHONE, FIRM_EMAIL, CORE_PRACTICE_AREAS, CLIENTS_SERVED } from "@/lib/content";

const phones = [MAIN_OFFICE_PHONE, FIRM_PHONE, PEAK_PHONE];

// Address parser: derives street, locality, region, postal from line1+line2.
// line2 is always "<City>, Texas <ZIP>"
function parseAddress(office) {
  const m = office.line2.match(/(.+?),\s*Texas\s+(\d{5})/i) || [];
  return {
    "@type": "PostalAddress",
    streetAddress: office.line1,
    addressLocality: m[1] || "",
    addressRegion: "TX",
    postalCode: m[2] || "",
    addressCountry: "US",
  };
}

function officePhones() {
  return phones.map((p) => ({
    "@type": "ContactPoint",
    telephone: p.href.replace("tel:", ""),
    contactType: p === PEAK_PHONE ? "PEAK American Financial — Clients & Representatives" : "customer service",
    areaServed: "US",
    availableLanguage: ["en", "hi"],
  }));
}

// Builds a `hasMap` Google Maps URL from a PostalAddress object.
function googleMapsUrl(office) {
  const q = encodeURIComponent(`${office.line1}, ${office.line2}`);
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

// Common pricing/payment fields for every LegalService node.
const COMMERCE_FIELDS = {
  priceRange: "$$$",
  paymentAccepted: ["Cash", "Check", "Credit Card", "Wire Transfer", "ACH"],
  currenciesAccepted: "USD",
};

// === Firm-level Organization / LegalService (Home, Locations index) ===
export function firmOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LegalService", "Attorney", "ProfessionalService"],
    "@id": `${SITE.url}/#organization`,
    name: SITE.name,
    legalName: SITE.legalName,
    description: SITE.description,
    slogan: SITE.slogan || SITE.tagline,
    foundingDate: SITE.foundingDate,
    numberOfEmployees: SITE.numberOfEmployees,
    keywords: SITE.keywords ? SITE.keywords.join(", ") : undefined,
    url: SITE.url,
    logo: SITE.logo,
    image: SITE.ogImage,
    dateModified: SITE.lastUpdated,
    ...COMMERCE_FIELDS,
    founder: {
      "@id": `${SITE.url}/about#darryl-pratt`,
    },
    employee: {
      "@id": `${SITE.url}/about#darryl-pratt`,
    },
    member: {
      "@id": `${SITE.url}/about#darryl-pratt`,
    },
    address: parseAddress(OFFICES[0]),
    location: OFFICES.map((o) => ({
      "@type": "Place",
      name: `${SITE.name} — ${o.region}`,
      address: parseAddress(o),
    })),
    areaServed: SITE.areasServed.map((n) => ({ "@type": "AdministrativeArea", name: n })),
    contactPoint: officePhones(),
    telephone: MAIN_OFFICE_PHONE.href.replace("tel:", ""),
    email: FIRM_EMAIL.display,
    sameAs: SITE.sameAs,
    knowsAbout: [
      "Business Law",
      "Series LLC formation",
      "Buy-sell agreements",
      "Business succession planning",
      "Mergers and acquisitions",
      "Chapter 11 Subchapter V",
      "Asset protection trusts",
      "Dynasty trusts",
      "Estate planning",
      "Probate",
      "Elder law",
      "Medicaid planning",
      "Cross-border U.S.–India tax and estate planning",
      "Pre-immigration tax planning",
      "Physician practice formation",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Core Practice Areas",
      itemListElement: CORE_PRACTICE_AREAS.map((c, idx) => ({
        "@type": "OfferCatalog",
        name: c.title,
        position: idx + 1,
        itemListElement: c.services.map((s, i) => ({
          "@type": "Offer",
          position: i + 1,
          itemOffered: { "@type": "Service", name: s, provider: { "@id": `${SITE.url}/#organization` } },
        })),
      })),
    },
    audience: CLIENTS_SERVED.map((c) => ({ "@type": "Audience", audienceType: c })),
  };
}

// === Per-Location LegalService (rich local SEO) ===
export function locationSchema(office) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE.url}/locations/${office.slug}#location`,
    name: `${SITE.name} — ${office.region}`,
    parentOrganization: { "@id": `${SITE.url}/#organization` },
    description: `${SITE.name} ${office.label || "office"} in ${office.line2.replace(/,\s*Texas/, ", Texas")} — serving ${office.serves}. ${office.headline}`,
    url: `${SITE.url}/locations/${office.slug}`,
    image: office.hero,
    telephone: MAIN_OFFICE_PHONE.href.replace("tel:", ""),
    address: parseAddress(office),
    hasMap: googleMapsUrl(office),
    areaServed: office.serves.split(" · ").map((n) => ({ "@type": "City", name: n.trim() })),
    contactPoint: officePhones(),
    knowsAbout: office.sectors,
    dateModified: SITE.lastUpdated,
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "09:00",
        closes: "17:00",
      },
    ],
    // Availability note surfaced for AI answer engines.
    availabilityStarts: "By appointment. Same-day scheduling available for retained clients.",
    ...COMMERCE_FIELDS,
  };
}

// === Person (Darryl Pratt) — About page ===
// Enhanced for Google Knowledge Panel: alma mater, memberOf, hasCredential
// with dates + issuer entities, and linked to firm's LegalService @id.
export function founderPersonSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["Person", "Attorney"],
    "@id": `${SITE.url}/about#darryl-pratt`,
    name: "Darryl V. Pratt",
    givenName: "Darryl",
    familyName: "Pratt",
    honorificSuffix: "Esq., CPA",
    jobTitle: "Managing Partner",
    description:
      "Founding Managing Partner of Lone Star Counsel — dually licensed as a Texas attorney and Certified Public Accountant with nearly 30 years of experience. Counsel to Texas business owners, physicians, franchise investors, and multi-generational families on formation, succession, asset protection, tax, restructuring, and legacy planning.",
    image: [
      "https://customer-assets.emergentagent.com/job_ralph-lauren-legal/artifacts/q9vyyfcg_darryl-pratt-frisco-tx.png",
      `${SITE.url}/img/darryl-environmental.jpg`,
      `${SITE.url}/img/founder-darryl.jpg`,
    ],
    url: `${SITE.url}/attorneys/darryl-v-pratt`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE.url}/attorneys/darryl-v-pratt`,
    },
    sameAs: SITE.sameAs,
    worksFor: { "@id": `${SITE.url}/#organization` },
    workLocation: { "@id": `${SITE.url}/locations/frisco#location` },
    memberOf: [
      { "@type": "Organization", name: "State Bar of Texas" },
      { "@type": "Organization", name: "State Bar of Texas — Business Law Section" },
      { "@type": "Organization", name: "State Bar of Texas — Bankruptcy Law Section" },
      { "@type": "Organization", name: "State Bar of Texas — General Practice, Solo & Small Firm Section" },
      { "@type": "Organization", name: "State Bar of Texas — Real Estate, Probate & Trust Law Section" },
      { "@type": "Organization", name: "Oklahoma Bar Association" },
      { "@type": "Organization", name: "American Institute of Certified Public Accountants (AICPA)" },
      { "@type": "Organization", name: "Texas Society of Certified Public Accountants" },
      { "@type": "Organization", name: "Oklahoma Society of Certified Public Accountants" },
      { "@type": "Organization", name: "American Bar Association" },
    ],
    alumniOf: [
      {
        "@type": "CollegeOrUniversity",
        name: "The University of Oklahoma",
        sameAs: "https://www.ou.edu/",
      },
      {
        "@type": "CollegeOrUniversity",
        name: "Oklahoma City University School of Law",
        sameAs: "https://law.okcu.edu/",
      },
    ],
    hasCredential: [
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "degree",
        name: "Bachelor of Business Administration (B.B.A.)",
        educationalLevel: "Bachelor",
        recognizedBy: { "@type": "CollegeOrUniversity", name: "The University of Oklahoma" },
        dateCreated: "1994",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "degree",
        name: "Juris Doctor (J.D.)",
        educationalLevel: "Doctoral",
        recognizedBy: { "@type": "CollegeOrUniversity", name: "Oklahoma City University School of Law" },
        dateCreated: "1997",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "License",
        name: "Attorney — State Bar of Texas",
        recognizedBy: { "@type": "Organization", name: "State Bar of Texas" },
        dateCreated: "1997",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "License",
        name: "Attorney — Oklahoma Bar Association",
        recognizedBy: { "@type": "Organization", name: "Oklahoma Bar Association" },
        dateCreated: "2015",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "License",
        name: "Certified Public Accountant (CPA) — Texas",
        recognizedBy: { "@type": "Organization", name: "Texas State Board of Public Accountancy" },
        dateCreated: "1997",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "License",
        name: "Certified Public Accountant (CPA) — Oklahoma",
        recognizedBy: { "@type": "Organization", name: "Oklahoma Accountancy Board" },
        dateCreated: "1997",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Admission",
        name: "U.S. Tax Court",
        dateCreated: "2001",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Admission",
        name: "U.S. District Court — Northern & Eastern Districts of Texas",
        dateCreated: "2001",
      },
      {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: "Admission",
        name: "U.S. Bankruptcy Court — Northern & Eastern Districts of Texas",
        dateCreated: "2001",
      },
    ],
    knowsAbout: [
      "Texas business law",
      "Business formation and entity structuring",
      "Series LLCs",
      "Buy-sell agreements",
      "Business succession planning",
      "Mergers and acquisitions",
      "Chapter 7, Chapter 11, and Subchapter V bankruptcy",
      "Trusts and estate planning",
      "Dynasty trusts and GST-exempt planning",
      "Asset protection strategies",
      "Probate and trust administration",
      "Guardianship and elder law",
      "Cross-border U.S.–India tax and estate planning",
      "Pre-immigration tax planning",
      "Physician practice formation",
      "Franchise structuring",
    ],
    knowsLanguage: ["en"],
    nationality: { "@type": "Country", name: "United States" },
    hasOccupation: [
      {
        "@type": "Occupation",
        name: "Attorney",
        occupationLocation: { "@type": "AdministrativeArea", name: "Texas" },
      },
      {
        "@type": "Occupation",
        name: "Certified Public Accountant",
        occupationLocation: { "@type": "AdministrativeArea", name: "Texas" },
      },
    ],
    subjectOf: {
      "@type": "WebPage",
      "@id": `${SITE.url}/about`,
      name: "About Darryl V. Pratt — Lone Star Counsel",
      url: `${SITE.url}/about`,
    },
  };
}

// === Article — for long-form Journal essays (JSON-LD Article schema) ===
export function articleSchema(article /* { title, slug, date, excerpt, cover, author, category, minutes } */) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription || article.excerpt,
    image: `${SITE.url}${article.ogImage || article.cover}`,
    author: {
      "@type": "Person",
      name: article.author,
      url: `${SITE.url}/about`,
    },
    publisher: { "@id": `${SITE.url}/#organization` },
    datePublished: article.datePublished || article.date,
    dateModified: article.datePublished || article.date,
    articleSection: article.category,
    wordCount: article.minutes * 200,
    mainEntityOfPage: `${SITE.url}/journal/${article.slug}`,
  };
}

// === Breadcrumb helper ===
export function breadcrumbSchema(trail /* [{name, path}] */) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: `${SITE.url}${t.path}`,
    })),
  };
}

// === WebSite + SearchAction (gets the "sitelinks search box" on Google) ===
export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.url}/#website`,
    url: SITE.url,
    name: SITE.name,
    description: SITE.shortDescription,
    publisher: { "@id": `${SITE.url}/#organization` },
    inLanguage: "en-US",
  };
}

// === FAQPage with Speakable (voice assistants read answers aloud) ===
export function faqPageSchema(items /* [{q, a}] */) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["[data-testid^=faq-answer-]", "[data-testid^=bankruptcy-faq-]"],
    },
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

// === WebPage wrapper — emitted on every page to give Google a clean
//   "this is the canonical primary entity of this URL" signal.
export function webPageSchema({ path, title, description, breadcrumbs }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE.url}${path === "/" ? "" : path}#webpage`,
    url: `${SITE.url}${path === "/" ? "" : path}`,
    name: title,
    description: description || SITE.description,
    isPartOf: { "@id": `${SITE.url}/#website` },
    about: { "@id": `${SITE.url}/#organization` },
    inLanguage: "en-US",
    dateModified: SITE.lastUpdated,
    primaryImageOfPage: { "@type": "ImageObject", url: SITE.ogImage },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-testid=hero-positioning]", "[data-testid=hero-subhead]"],
    },
    breadcrumb: breadcrumbs ? { "@id": `${SITE.url}${path === "/" ? "" : path}#breadcrumb` } : undefined,
  };
}

// === Service schema per Continuum stage (Build / Grow / Protect / Restructure / Transition / Preserve) ===
export function continuumServiceSchemas(practiceAreas /* PRACTICE_AREAS from content.js */) {
  return practiceAreas.map((p) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE.url}/practice#${p.slug}`,
    serviceType: p.title,
    name: `${p.title} — ${p.serif}`,
    description: p.detail || p.blurb || p.serif,
    provider: { "@id": `${SITE.url}/#organization` },
    areaServed: { "@type": "AdministrativeArea", name: "Texas" },
    url: `${SITE.url}/practice`,
    category: "Legal Services",
  }));
}


// === Authored practice-page schema =========================================
// Emits two nodes:
//   1) A `WebPage` with `author`, `datePublished`, `dateModified`, `wordCount`,
//      and `about` pointing at the practice discipline.
//   2) The full Person entity for Darryl V. Pratt (via founderPersonSchema)
//      so Google resolves the `@id` referenced from `author`.
// This is the E-E-A-T signal Google increasingly weights for legal content —
// pages become authored articles rather than anonymous service listings.
export function authoredPracticePageSchema({
  path,
  title,
  description,
  discipline,
  minutes,
  lastReviewed,
  publishedDate = "2024-01-01",
} = {}) {
  const url = `${SITE.url}${path}`;
  const wordCount = typeof minutes === "number" ? minutes * 200 : undefined;

  const webPageNode = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#authored-webpage`,
    url,
    name: title,
    description,
    inLanguage: SITE.locale || "en_US",
    isPartOf: { "@id": `${SITE.url}/#website` },
    author: { "@id": `${SITE.url}/about#darryl-pratt` },
    creator: { "@id": `${SITE.url}/about#darryl-pratt` },
    editor: { "@id": `${SITE.url}/about#darryl-pratt` },
    publisher: { "@id": `${SITE.url}/#organization` },
    datePublished: publishedDate,
    dateModified: lastReviewed || SITE.lastUpdated,
    reviewedBy: { "@id": `${SITE.url}/about#darryl-pratt` },
    lastReviewed: lastReviewed || SITE.lastUpdated,
    about: discipline
      ? {
          "@type": "Thing",
          name: discipline,
        }
      : undefined,
    mainContentOfPage: {
      "@type": "WebPageElement",
      cssSelector: "main",
    },
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2", "[data-testid='practice-byline']"],
    },
    wordCount,
    accessMode: ["textual", "visual"],
    accessibilityFeature: ["highContrast", "readingOrder"],
  };

  // The full founder Person entity — attached so the author @id resolves.
  const personNode = founderPersonSchema();

  return [webPageNode, personNode];
}

/**
 * LegalService node — used on flagship service pages to reinforce the
 * firm's authority for a specific offering (e.g. Asset Purchase Agreements).
 */
export function legalServiceSchema({ name, description, url, area = "Texas" }) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    name,
    description,
    url,
    provider: { "@id": "https://lonestarcounsel.com/#organization" },
    areaServed: { "@type": "State", name: area },
    priceRange: "$$$$",
    telephone: "+1-972-712-1515",
  };
}

/**
 * Attorney node — surfaces Darryl V. Pratt as the responsible attorney
 * for the page in Google's Knowledge Graph.
 */
export function attorneySchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Attorney",
    name: "Darryl V. Pratt",
    jobTitle: "Managing Partner — Attorney, CPA, Business Advisor",
    worksFor: { "@id": "https://lonestarcounsel.com/#organization" },
    url: "https://lonestarcounsel.com/attorneys/darryl-v-pratt",
    knowsAbout: [
      "Asset Purchase Agreements",
      "Business Acquisitions & Sales",
      "Business Succession Planning",
      "Estate Planning",
      "Probate",
      "Entity Formation",
    ],
  };
}

